import { DefaultOptionComponent } from 'components/DefaultOptionComponent';
import { isEqual } from 'lodash-es';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type {
  ActionListContextProps,
  ActionListContextValues,
  ActionListFullValueInternal,
  ActionListItem,
  ActionListOption,
  ActionListValueInternal,
} from './types';

// oxlint-disable-next-line typescript-eslint/no-explicit-any -- intentional: cast happens at useActionListContext boundary
const ActionListContext = createContext<ActionListContextValues<any, any, any>>(
  {
    onChange: () => {},
    onSetValues: () => {},
    OptionComponent: DefaultOptionComponent,
    selectedValues: undefined,
    filteredOptions: [],
    optionMap: new Map(),
    inlineLoading: false,
    showSelectedValue: true,
  },
);

const defaultSearchFn = <T,>(
  opt: ActionListOption<T>,
  searchTerm: string,
  searchByLabelOnly?: boolean,
) => {
  // Sanitize the search term to prevent regex injection, and invalid regex patterns
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`.*${escapedSearchTerm}.*`, 'gi');
  if ('title' in opt) {
    // Check if the group title matches
    const titleMatches = opt.title.match(regex);

    // Filter items that match
    const newSubItems = opt.items.filter((item) => {
      const labelMatches = item.label.match(regex);
      return searchByLabelOnly
        ? labelMatches
        : labelMatches || item.value.match(regex);
    });
    // Return the group if either the title matches or there are matching sub-items
    // If title matches, include all items; if only sub-items match, include only those
    if (titleMatches) {
      return opt; // Return the full group with all items
    } else if (newSubItems.length > 0) {
      return { ...opt, items: newSubItems }; // Return group with filtered items
    }

    return undefined;
  }

  const labelMatches = opt.label.match(regex);
  const matches = searchByLabelOnly
    ? labelMatches
    : labelMatches || opt.value.match(regex);
  return matches ? opt : undefined;
};

export const useActionListContext = <
  isMulti extends boolean = false,
  isNullable extends boolean = false,
  T = undefined,
>() =>
  useContext(ActionListContext) as ActionListContextValues<
    isMulti,
    isNullable,
    T
  >;

export const ActionListProvider = <
  isMulti extends boolean = false,
  isNullable extends boolean = false,
  T = undefined,
>({
  OptionComponent,
  isMulti,
  isNullable,
  allowSelectAll,
  onChange: passedOnChange,
  options,
  value,
  children,
  searchTerm,
  isExternalSearch = false,
  inlineLoading = false,
  searchFn = defaultSearchFn,
  selectionElementPosition,
  showSelectedValue = true,
  searchByLabelOnly = false,
}: ActionListContextProps<isMulti, isNullable, T>) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const optionMap: Map<string, ActionListItem<T>> = useMemo(() => {
    return options
      .flatMap((opt) => {
        if ('items' in opt) {
          return opt.items;
        }

        return [opt];
      })
      .reduce<Map<string, ActionListItem<T>>>((acc, opt) => {
        if (!acc.has(opt.value)) {
          acc.set(opt.value, opt);
        }

        return acc;
      }, new Map());
  }, [options]);

  // Cast once: passedOnChange uses conditional generic types that TS can't resolve
  // internally. We cast to the internal union type so runtime narrowing works below.
  const typedOnChange = passedOnChange as (
    value: ActionListValueInternal,
    fullValue: ActionListFullValueInternal<T>,
  ) => void;

  const onChange = useCallback(
    (selectedValue?: string) => {
      if (!selectedValue) {
        typedOnChange(isMulti ? [] : null, isMulti ? [] : null);
        return;
      }

      if (!isMulti && selectedValue === value && isNullable) {
        typedOnChange(null, null);
        return;
      }

      if (!isMulti && selectedValue) {
        typedOnChange(selectedValue, optionMap.get(selectedValue));
        return;
      }

      const selectedValuesSet = new Set(value as Iterable<string>);

      if (selectedValuesSet.has(selectedValue)) {
        selectedValuesSet.delete(selectedValue);
      } else {
        selectedValuesSet.add(selectedValue);
      }

      const valueArr = Array.from(selectedValuesSet);
      const fullValueArr = valueArr
        .map((val) => optionMap.get(val))
        .filter((v): v is ActionListItem<T> => v != null);

      typedOnChange(valueArr, fullValueArr);
    },
    [typedOnChange, value, isMulti, optionMap, isNullable],
  );

  const onSetValues = useCallback(
    (selectedValues: string[]) => {
      if (isEqual(value as string[] | undefined, selectedValues)) {
        return;
      }

      const fullValueArr = selectedValues
        .map((val) => optionMap.get(val))
        .filter((v): v is ActionListItem<T> => v != null);

      typedOnChange(selectedValues, fullValueArr);
    },
    [optionMap, typedOnChange, value],
  );

  useEffect(() => {
    if (!searchTerm || !searchTerm.length || isExternalSearch) {
      setFilteredOptions(options);
      return;
    }

    // oxlint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const newFilteredOptions: ActionListOption<T>[] = options
      // need to use map since options could be a title with sub items
      .map((opt) => searchFn(opt, searchTerm, searchByLabelOnly))
      // For some reason the if I don't cast here CI complains about types
      // Saying undefined is not assignable
      .filter((opt) => !!opt) as ActionListOption<T>[];
    setFilteredOptions(newFilteredOptions);
  }, [searchTerm, options, isExternalSearch, searchFn, searchByLabelOnly]);

  const contextValue: ActionListContextValues<isMulti, isNullable, T> = useMemo(
    () => ({
      filteredOptions,
      isMulti,
      isNullable,
      allowSelectAll,
      onChange,
      onSetValues,
      OptionComponent,
      selectedValues: value,
      optionMap,
      inlineLoading,
      selectionElementPosition,
      showSelectedValue,
    }),
    [
      allowSelectAll,
      filteredOptions,
      inlineLoading,
      isMulti,
      isNullable,
      onChange,
      onSetValues,
      OptionComponent,
      optionMap,
      selectionElementPosition,
      showSelectedValue,
      value,
    ],
  );
  return (
    <ActionListContext.Provider value={contextValue}>
      {children}
    </ActionListContext.Provider>
  );
};
